import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { listaModulos } from '../data/modulos';
import './FeatureDetails.css';

const ModuleDetail = ({ modulo }) => {
  const navigate = useNavigate();
  const [abaAtiva, setAbaAtiva] = useState('funcionalidades');

  if (!modulo) {
    return (
      <div className="module-detail-error">
        <div className="container">
          <h2>Módulo não encontrado</h2>
          <Link to="/funcionalidades" className="btn btn-primary">
            Voltar para Funcionalidades
          </Link>
        </div>
      </div>
    );
  }

  // Encontrar índice do módulo atual para navegação
  const moduloIndex = listaModulos.findIndex(m => m.id === modulo.id);
  const moduloAnterior = moduloIndex > 0 ? listaModulos[moduloIndex - 1] : null;
  const proximoModulo = moduloIndex < listaModulos.length - 1 ? listaModulos[moduloIndex + 1] : null;

  return (
    <div className="module-detail-page">
      <SEO
        title={`${modulo.titulo} - Veritux ERP`}
        description={modulo.descricao}
        keywords={`${modulo.titulo}, ERP, sistema de gestão, ${modulo.funcionalidades.map(f => f.nome).join(', ')}`}
        url={`https://www.veritux.com.br/funcionalidades/${modulo.id}`}
      />

      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <div className="container">
          <Link to="/">Início</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/funcionalidades">Funcionalidades</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{modulo.titulo}</span>
        </div>
      </nav>

      {/* Header do Módulo */}
      <header className="module-detail-header">
        <div className="container">
          <Link to="/funcionalidades" className="back-link">
            ← Voltar para Funcionalidades
          </Link>
          <div className="module-detail-title-section">
            <div className="module-detail-icon">{modulo.icone}</div>
            <div>
              <h1 className="module-detail-title">{modulo.titulo}</h1>
              <p className="module-detail-subtitle">{modulo.descricao}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Navegação por Abas */}
      <div className="module-tabs">
        <div className="container">
          <button
            className={`module-tab ${abaAtiva === 'funcionalidades' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('funcionalidades')}
          >
            Funcionalidades
          </button>
          <button
            className={`module-tab ${abaAtiva === 'cadastros' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('cadastros')}
          >
            Cadastros
          </button>
          <button
            className={`module-tab ${abaAtiva === 'operacoes' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('operacoes')}
          >
            Operações
          </button>
          <button
            className={`module-tab ${abaAtiva === 'relatorios' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('relatorios')}
          >
            Relatórios
          </button>
        </div>
      </div>

      {/* Conteúdo das Abas */}
      <section className="module-content">
        <div className="container">
          {/* Aba Funcionalidades */}
          {abaAtiva === 'funcionalidades' && (
            <div className="module-tab-content">
              <h2 className="module-section-title">Funcionalidades Principais</h2>
              <div className="funcionalidades-grid">
                {modulo.funcionalidades.map((funcionalidade, index) => (
                  <div key={index} className="funcionalidade-card">
                    <h3 className="funcionalidade-title">{funcionalidade.nome}</h3>
                    <p className="funcionalidade-description">{funcionalidade.descricao}</p>
                  </div>
                ))}
              </div>

              {/* Detalhes Expandidos */}
              {modulo.detalhes && Object.keys(modulo.detalhes).length > 0 && (
                <div className="module-details-expanded">
                  <h2 className="module-section-title">Detalhes das Funcionalidades</h2>
                  {Object.entries(modulo.detalhes).map(([key, value]) => (
                    <div key={key} className="detail-expanded-item">
                      <h3 className="detail-expanded-title">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </h3>
                      <p className="detail-expanded-text">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aba Cadastros */}
          {abaAtiva === 'cadastros' && (
            <div className="module-tab-content">
              <h2 className="module-section-title">Cadastros Disponíveis</h2>
              <ul className="module-list">
                {modulo.cadastros.map((cadastro, index) => (
                  <li key={index} className="module-list-item">
                    <span className="list-icon">✓</span>
                    {cadastro}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Aba Operações */}
          {abaAtiva === 'operacoes' && (
            <div className="module-tab-content">
              <h2 className="module-section-title">Operações Disponíveis</h2>
              <ul className="module-list">
                {modulo.operacoes.map((operacao, index) => (
                  <li key={index} className="module-list-item">
                    <span className="list-icon">→</span>
                    {operacao}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Aba Relatórios */}
          {abaAtiva === 'relatorios' && (
            <div className="module-tab-content">
              <h2 className="module-section-title">Relatórios Disponíveis</h2>
              <ul className="module-list">
                {modulo.relatorios.map((relatorio, index) => (
                  <li key={index} className="module-list-item">
                    <span className="list-icon">📊</span>
                    {relatorio}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Navegação entre Módulos */}
      <section className="module-navigation">
        <div className="container">
          <div className="module-nav-grid">
            {moduloAnterior && (
              <div 
                className="module-nav-card prev"
                onClick={() => navigate(`/funcionalidades/${moduloAnterior.id}`)}
              >
                <span className="module-nav-label">← Anterior</span>
                <h3 className="module-nav-title">{moduloAnterior.titulo}</h3>
              </div>
            )}
            {proximoModulo && (
              <div 
                className="module-nav-card next"
                onClick={() => navigate(`/funcionalidades/${proximoModulo.id}`)}
              >
                <span className="module-nav-label">Próximo →</span>
                <h3 className="module-nav-title">{proximoModulo.titulo}</h3>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="module-cta">
        <div className="container">
          <h2 className="module-cta-title">Quer saber mais sobre o Veritux ERP?</h2>
          <p className="module-cta-subtitle">
            Entre em contato conosco e descubra como podemos ajudar sua empresa
          </p>
          <div className="module-cta-buttons">
            <a href="/downloads" className="btn btn-primary btn-large">
              Teste Grátis
            </a>
            <a 
              href={`https://wa.me/5543996782197?text=Olá! Gostaria de saber mais sobre o módulo ${modulo.titulo} do Veritux ERP.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-large"
            >
              Falar com Especialista
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModuleDetail;
